import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import useResponsive from '../../hooks/useResponsive';

// ----------------------------------------------------------------------
// Shared loading placeholders used across list pages, route fallbacks,
// and detail dialogs.
// ----------------------------------------------------------------------

function MobileSkeletonCard() {
  return (
    <Card variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
      <Stack spacing={1.25}>
        <Skeleton variant="text" width="35%" height={14} />
        <Skeleton variant="text" width="55%" height={28} />
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="65%" />
      </Stack>
    </Card>
  );
}

export function TableSkeleton({ rows, columns, headers, minWidth }) {
  const isMobile = useResponsive('down', 'md');
  const columnCount = headers?.length || columns;

  if (isMobile) {
    return (
      <Stack spacing={1.5} sx={{ p: 1.5 }}>
        {Array.from({ length: rows }).map((_, index) => (
          <MobileSkeletonCard key={index} />
        ))}
      </Stack>
    );
  }

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table sx={minWidth ? { minWidth } : undefined} size="small">
        {headers?.length > 0 && (
          <TableHead>
            <TableRow>
              {headers.map((label, index) => (
                <TableCell key={index} align="center">
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <TableRow key={rowIndex}>
              {Array.from({ length: columnCount }).map((__, colIndex) => (
                <TableCell key={colIndex} align="center">
                  <Skeleton variant="text" width={`${55 + ((rowIndex + colIndex) % 4) * 10}%`} sx={{ mx: 'auto' }} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

TableSkeleton.propTypes = {
  rows: PropTypes.number,
  columns: PropTypes.number,
  headers: PropTypes.arrayOf(PropTypes.node),
  minWidth: PropTypes.number,
};

TableSkeleton.defaultProps = {
  rows: 8,
  columns: 6,
};

export function DetailSkeleton({ rows }) {
  return (
    <Stack spacing={2} sx={{ py: 1 }}>
      {Array.from({ length: rows }).map((_, index) => (
        <Box key={index}>
          <Skeleton variant="text" width="30%" height={14} />
          <Skeleton variant="text" width={`${60 + (index % 3) * 12}%`} height={24} />
        </Box>
      ))}
    </Stack>
  );
}

DetailSkeleton.propTypes = {
  rows: PropTypes.number,
};

DetailSkeleton.defaultProps = {
  rows: 6,
};

export function PageSkeleton() {
  return (
    <Box sx={{ width: '100%', py: 1 }}>
      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Skeleton variant="text" width={220} height={40} />
        <Skeleton variant="text" width={320} height={20} />
      </Stack>

      <Card sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: 'repeat(4, 1fr)',
            },
          }}
        >
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} variant="rounded" height={40} />
          ))}
        </Box>
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Skeleton variant="rounded" width={88} height={36} />
          <Skeleton variant="rounded" width={96} height={36} />
        </Stack>
      </Card>

      <Card>
        <TableSkeleton
          rows={8}
          headers={['SNo', 'Name', 'Details', 'Status', 'Date', 'Actions']}
        />
      </Card>
    </Box>
  );
}

export default TableSkeleton;
